import { Button, FormControlLabel, Switch, Typography } from '@mui/material'
import { useCallback } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../../models'
import { InspectionWithRelated } from '../../../models/inspections'
import { useUpdateInspectionMutation } from '../../../redux/api/inspectionApi'
import { checkSingleEntryIsCompleteOrNotRequired } from '../../../utils/checkInspectionEntryRequiredNotesOrPhoto'
import { CompleteInspectionButtonCached } from './CompleteInspectionButtonCached'

const CompleteInspectionButton = ({
  showIncomplete,
  setShowIncomplete,
  inspection,
  isCached
}: {
  showIncomplete: boolean
  setShowIncomplete: (s: boolean) => void
  inspection: InspectionWithRelated | undefined
  isCached?: boolean
}) => {
  const { id } = useParams()
  const { accessToken } = useAccessTokenAndShopId()
  const navigate = useNavigate()

  const [updateInspection, { isLoading }] = useUpdateInspectionMutation()

  const handleCompleteInspection = useCallback(async () => {
    if (!inspection) {
      return
    }
    const updatedEntry = {
      id: inspection.id!,
      info: {
        ...inspection.info!,
        finished: new Date()
      }
    }
    await updateInspection({
      authorization: accessToken,
      inspectionEntry: updatedEntry
    })
    navigate(generatePath(AppRoutes.DVI_SINGLE_INSPECTION_SUMMARY, { id: id! }))
  }, [accessToken, id, inspection, navigate, updateInspection])
  if (!inspection) {
    return <></>
  }
  if (inspection.info?.finished) {
    return <></>
  }
  const entries = inspection.sections.flatMap((s) => s.entries)
  const incompleteEntries = entries.filter(
    (entry) => !checkSingleEntryIsCompleteOrNotRequired(entry)
  )

  return (
    <>
      {isCached ? (
        <CompleteInspectionButtonCached
          disabled={isLoading || incompleteEntries.length !== 0}
          inspection={inspection}
        />
      ) : (
        <Button
          variant="contained"
          fullWidth
          onClick={handleCompleteInspection}
          disabled={isLoading || incompleteEntries.length !== 0}>
          Complete Inspection
        </Button>
      )}
      {incompleteEntries.length !== 0 && (
        <Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showIncomplete}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setShowIncomplete(event.target.checked)
                }}
              />
            }
            label="Show only incomplete required entries"
          />
          <Typography color="error.main" component={'span'}>
            {incompleteEntries.length} required entries are not answered or
            missing notes or photos. Please finish the required entries before
            complete the inspection.
          </Typography>
        </Typography>
      )}
    </>
  )
}

export default CompleteInspectionButton
